import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, post, fileUrl } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Badge } from "@/components/common";
import { useAccess } from "@/lib/access";
import { Layers, ChevronLeft, ListChecks, Clock, Trophy, Lock, Zap, ArrowRight, FileText, BarChart3, LayoutGrid, BookOpen, Landmark, Shield, GraduationCap, TrainFront, Users, Building2 } from "lucide-react";

const THEMES = [
  { card: "from-blue-50", icon: "bg-blue-100 text-blue-600", btn: "bg-blue-600 hover:bg-blue-700", text: "text-blue-600" },
  { card: "from-orange-50", icon: "bg-orange-100 text-orange-600", btn: "bg-orange-500 hover:bg-orange-600", text: "text-orange-600" },
  { card: "from-violet-50", icon: "bg-violet-100 text-violet-600", btn: "bg-violet-600 hover:bg-violet-700", text: "text-violet-600" },
  { card: "from-emerald-50", icon: "bg-emerald-100 text-emerald-600", btn: "bg-emerald-600 hover:bg-emerald-700", text: "text-emerald-600" },
  { card: "from-rose-50", icon: "bg-rose-100 text-rose-600", btn: "bg-rose-500 hover:bg-rose-600", text: "text-rose-600" },
  { card: "from-sky-50", icon: "bg-sky-100 text-sky-600", btn: "bg-sky-600 hover:bg-sky-700", text: "text-sky-600" },
  { card: "from-amber-50", icon: "bg-amber-100 text-amber-600", btn: "bg-amber-500 hover:bg-amber-600", text: "text-amber-600" },
];
const ART = [
  [/10|class|school|board/i, "books", BookOpen], [/bank|insurance/i, "bank", Landmark], [/civil|psc|upsc/i, "civil", Building2],
  [/defen|army|nda/i, "defence", Shield], [/rail|rrb/i, "railways", TrainFront], [/ssc/i, "ssc", Users], [/teach|tet/i, "teaching", GraduationCap],
];
const artFor = (name) => ART.find(([re]) => re.test(name || "")) || [null, "books", Layers];
const fmtNum = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : String(n));

function descendants(node) {
  let ids = [node.id];
  (node.children || []).forEach((c) => { ids = ids.concat(descendants(c)); });
  return ids;
}

export default function TestSeries() {
  const nav = useNavigate();
  const [path, setPath] = useState([]); // array of nodes drilled into
  const { data: tree, isLoading } = useQuery({ queryKey: ["tax-tree"], queryFn: () => get("/taxonomy/tree") });
  const { data: mocks } = useQuery({ queryKey: ["ts-mock"], queryFn: () => get("/tests?test_type=mock") });
  const { data: pyps } = useQuery({ queryKey: ["ts-pyp"], queryFn: () => get("/tests?test_type=pyp") });
  const { data: subs } = useQuery({ queryKey: ["mysubs"], queryFn: () => get("/my/subscriptions") });
  const access = useAccess();
  const subscribed = (subs || []).some((s) => s.status === "active");

  const [chip, setChip] = useState("all");
  const node = path[path.length - 1];
  const children = node ? (node.children || []) : (tree || []);
  const isExamPage = node && (node.level !== "category" || children.length === 0);
  const allTests = useMemo(() => [...(mocks || []), ...(pyps || [])], [mocks, pyps]);
  const statsFor = (c) => { const ids = descendants(c); const ts = allTests.filter((t) => ids.includes(t.exam_id)); return { tests: ts.length, attempts: ts.reduce((s, t) => s + (t.attempts_count || 0), 0) }; };
  const shownCats = !node && chip !== "all" ? children.filter((c) => c.id === chip) : children;
  const rootId = path[0]?.id;

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">Test Series</h1>
          <p className="text-slate-500 mt-2">Pick your exam and start practicing in a real exam environment</p>
        </div>
        <div className="hidden md:flex items-center gap-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-violet-100 border border-violet-100 pl-5 pr-2 py-2 max-w-md" data-testid="ts-banner">
          <div><p className="font-display font-extrabold text-indigo-800 text-lg leading-tight">Practice Smarter<br />Score Higher</p><p className="text-xs text-slate-500 mt-1">Full-length tests, section-wise tests and real exam pattern.</p></div>
          <img src="/illustrations/trophy.jpg" alt="" className="h-24 w-36 object-cover rounded-xl" />
        </div>
      </div>
      {!node && children.length > 0 && (
        <div className="flex gap-2 overflow-x-auto scroll-hide pb-2 mb-4" data-testid="ts-chips">
          <Chip active={chip === "all"} onClick={() => setChip("all")} Icon={LayoutGrid} label="All" testid="ts-chip-all" />
          {children.map((c) => <Chip key={c.id} active={chip === c.id} onClick={() => setChip(c.id)} Icon={artFor(c.name)[2]} label={c.name} testid={`ts-chip-${c.id}`} />)}
        </div>
      )}
      {path.length > 0 && (
        <button onClick={() => setPath(path.slice(0, -1))} data-testid="ts-back-btn" className="text-sm text-slate-500 flex items-center gap-1 mb-4"><ChevronLeft className="h-4 w-4" /> Back</button>
      )}
      {!isExamPage ? (
        children.length === 0 ? <EmptyState icon={Layers} title="No exams configured yet" /> : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {shownCats.map((c, i) => <CategoryCard key={c.id} c={c} theme={THEMES[i % THEMES.length]} stats={statsFor(c)} subscribed={access.hasCategory(rootId || c.id)} onOpen={() => setPath([...path, c])} />)}
          </div>
        )
      ) : (
        <ExamPage node={node} mocks={mocks || []} pyps={pyps || []} canTest={access.canTest} nav={nav} />
      )}
    </div>
  );
}

function Chip({ active, onClick, Icon, label, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`st-chip shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border ${active ? "bg-indigo-600 border-indigo-600 text-white shadow-md" : "bg-white border-slate-200 text-slate-700 hover:border-indigo-300"}`}><Icon className="h-4 w-4" /> {label}</button>;
}

function CategoryCard({ c, theme, stats, subscribed, onOpen }) {
  const [, art, Icon] = artFor(c.name);
  const desc = c.description || ((c.children || []).length ? `${(c.children || []).slice(0, 4).map((x) => x.name).join(", ")}${c.children.length > 4 ? " and more" : ""}` : "Full syllabus tests with real exam pattern");
  return (
    <div className={`relative rounded-3xl border border-slate-200 bg-gradient-to-br ${theme.card} to-white p-5 card-hover flex flex-col`} data-testid={`ts-cat-${c.id}`}>
      <button onClick={onOpen} aria-label="Open" className={`absolute top-4 right-4 h-8 w-8 rounded-full bg-white shadow flex items-center justify-center ${theme.text}`}><ArrowRight className="h-4 w-4" /></button>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${theme.icon}`}>
            {c.logo ? <img src={fileUrl(c.logo.replace("/api", ""))} alt="" className="h-7 w-7 rounded object-cover" /> : <Icon className="h-5 w-5" />}
          </div>
          <h3 className="font-display font-extrabold text-xl text-slate-900 mt-4 leading-snug">{c.name}</h3>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{desc}</p>
        </div>
        <img src={`/illustrations/${art}.jpg`} alt="" className="h-24 w-24 rounded-2xl object-cover mt-8 shadow-sm" />
      </div>
      <div className="flex items-center gap-3 text-sm text-slate-600 mt-4" data-testid={`ts-cat-stats-${c.id}`}>
        <span className="flex items-center gap-1"><FileText className={`h-4 w-4 ${theme.text}`} /><b className="text-slate-800">{stats.tests}</b> Tests</span>
        <span className="text-slate-300">|</span>
        <span className="flex items-center gap-1"><BarChart3 className={`h-4 w-4 ${theme.text}`} /><b className="text-slate-800">{fmtNum(stats.attempts)}</b> Attempts</span>
      </div>
      <button onClick={onOpen} data-testid={`ts-open-${c.id}`} className={`mt-4 w-full text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 ${theme.btn}`}>
        {subscribed ? "Go To Test Series" : "Enroll Now"} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function ExamPage({ node, mocks, pyps, canTest, nav }) {
  const [tab, setTab] = useState("mock");
  const [kind, setKind] = useState("full"); // full | sectional
  const ids = useMemo(() => descendants(node), [node]);
  const inScope = (list) => list.filter((t) => ids.includes(t.exam_id));
  const mockList = inScope(mocks);
  const pypList = inScope(pyps);
  const active = tab === "mock" ? mockList : pypList;
  const full = active.filter((t) => t.format !== "split");
  const sectional = active.filter((t) => t.format === "split");
  const shown = kind === "full" ? full : sectional;

  const start = async (t) => {
    if (!canTest(t)) { toast.info("Your plan doesn't cover this exam — subscribe to unlock"); nav("/app/subscriptions"); return; }
    try { const r = await post(`/tests/${t.id}/start`); nav(`/exam/${r.attempt_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Unavailable"); }
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
        <h2 className="font-display text-xl font-extrabold text-slate-900">{node.name}</h2>
        <p className="text-sm text-slate-500">Online test series for {node.name} — {mockList.length} mocks & {pypList.length} previous year papers.</p>
      </div>
      <div className="flex gap-2 mb-3">
        <button onClick={() => setTab("mock")} data-testid="ts-tab-mock" className={`px-5 py-2 rounded-full text-sm font-bold ${tab === "mock" ? "bg-indigo-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Mocks ({mockList.length})</button>
        <button onClick={() => setTab("pyp")} data-testid="ts-tab-pyp" className={`px-5 py-2 rounded-full text-sm font-bold ${tab === "pyp" ? "bg-indigo-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Previous Year ({pypList.length})</button>
      </div>
      <div className="flex gap-4 border-b border-slate-200 mb-4">
        {[["full", `Full Test (${full.length})`], ["sectional", `Sectional (${sectional.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`ts-kind-${k}`} className={`pb-2 text-sm font-bold border-b-2 ${kind === k ? "border-indigo-800 text-indigo-800" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {shown.length === 0 ? <EmptyState icon={ListChecks} title="No tests here yet" /> : (
        <div className="space-y-3">
          {shown.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center gap-4" data-testid={`test-row-${t.id}`}>
              <div className="flex-1 min-w-[240px]">
                <h3 className="font-display font-bold text-slate-900">{t.title} {t.is_free && <Badge tone="green">Free</Badge>}</h3>
                <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                  <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {t.total_questions} Questions</span>
                  <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" /> {t.total_marks} Marks</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.duration_min} Mins</span>
                </div>
              </div>
              {canTest(t)
                ? <button onClick={() => start(t)} data-testid={`start-test-${t.id}`} className="bg-indigo-800 hover:bg-indigo-900 text-white font-bold px-6 py-2.5 rounded-lg flex items-center gap-2"><Zap className="h-4 w-4" /> Start Now</button>
                : <button onClick={() => start(t)} data-testid={`unlock-test-${t.id}`} className="bg-sky-500 hover:bg-sky-600 text-white font-bold px-6 py-2.5 rounded-lg flex items-center gap-2"><Lock className="h-4 w-4" /> Unlock Now</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
